import { useState, type MouseEvent } from "react";
import { format } from "date-fns";
import { de, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import {
  MapPin,
  Calendar,
  User,
  Users,
  UserPlus,
  MoreVertical,
  Play,
  Eye,
  Pencil,
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Trash2,
  AlertTriangle,
  FileCheck,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/status-badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { ReopenInspectionDialog } from "./ReopenInspectionDialog";
import { getProfileDisplayName } from "@/lib/profileUtils";
import { ProfileAvatar } from "@/components/common/ProfileAvatar";
import type { InspectionStatus } from "@/types";
import { buildInspectionCardActionModel } from "./inspectionCardActions";

interface ProfileInfo {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  display_name?: string | null;
  avatar_url?: string | null;
}

interface AssignableUser {
  user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  display_name?: string | null;
}

export interface InspectionCardData {
  id: string;
  status: InspectionStatus;
  due_date: string | null;
  assigned_to: string | null;
  created_by?: string | null;
  position_id?: string | null;
  notes?: string | null;
  completed_at?: string | null;
  defect_count?: number;
  team_id?: string | null;
  team_name?: string | null;
  checklists: {
    name: string;
    version?: string | null;
  };
  inspection_positions?: {
    name: string;
    is_archived?: boolean;
  } | null;
  assigned_profile?: ProfileInfo | null;
  created_profile?: ProfileInfo | null;
  /** Flag indicating the assigned user is disabled or no longer exists */
  isAssigneeUnavailable?: boolean;
}

interface InspectionCardProps {
  inspection: InspectionCardData;
  /** Current user ID for permission checks */
  currentUserId?: string;
  /** Whether the current user is a QM */
  isQualitaetsmanager?: boolean;
  /** Callback for start action */
  onStart?: (id: string) => void;
  /** Callback for continue action */
  onContinue?: (id: string) => void;
  /** Callback for view action */
  onView?: (id: string) => void;
  /** Callback for edit action */
  onEdit?: (inspection: InspectionCardData) => void;
  /** Callback for reopen action */
  onReopen?: (id: string) => void;
  /** Callback for cancel action */
  onCancel?: (id: string) => void;
  /** Callback for delete action - receives id and checklist name for confirmation dialog */
  onDelete?: (id: string, checklistName: string) => void;
  /** Callback for assign action */
  onAssign?: (id: string, userId: string) => void;
  /** List of assignable users */
  assignableUsers?: AssignableUser[];
  /** Optional: Show menu with actions */
  showMenu?: boolean;
  /** Optional: Compact mode */
  compact?: boolean;
}

export function InspectionCard({
  inspection,
  currentUserId,
  isQualitaetsmanager = false,
  onStart,
  onContinue,
  onView,
  onEdit,
  onReopen,
  onCancel,
  onDelete,
  onAssign,
  assignableUsers = [],
  showMenu = true,
  compact = false,
}: InspectionCardProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? enUS : de;
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [isReopening, setIsReopening] = useState(false);

  const actionModel = buildInspectionCardActionModel({
    inspection,
    currentUserId,
    isQualitaetsmanager,
    callbacks: {
      start: Boolean(onStart),
      continue: Boolean(onContinue),
      view: Boolean(onView),
      edit: Boolean(onEdit),
      reopen: Boolean(onReopen),
      cancel: Boolean(onCancel),
      delete: Boolean(onDelete),
      assign: Boolean(onAssign),
    },
    assignableUserCount: assignableUsers.length,
    showMenu,
  });
  const { displayStatus, isOverdue } = actionModel;
  const checklistName = inspection.checklists?.name
    ? `${inspection.checklists.name}${inspection.checklists.version ? ` (v${inspection.checklists.version})` : ""}`
    : t("dashboard.unknownChecklist");
  const positionName = inspection.inspection_positions?.name;

  const getProfileName = (profile?: ProfileInfo | null) => {
    if (!profile) return null;
    return getProfileDisplayName(profile);
  };

  const getAssignableUserName = (user: AssignableUser): string => getProfileDisplayName(user);

  const assignedName = getProfileName(inspection.assigned_profile);
  const createdName = getProfileName(inspection.created_profile);
  const renderAssignableUserItems = () =>
    assignableUsers.map((user) => (
      <DropdownMenuItem
        key={user.user_id}
        onClick={() => onAssign!(inspection.id, user.user_id)}
        data-sentry-mask
      >
        <User className="h-4 w-4 mr-2" />
        {getAssignableUserName(user)}
      </DropdownMenuItem>
    ));

  const handleReopenClick = () => {
    setReopenDialogOpen(true);
  };

  const handleReopenConfirm = async () => {
    if (!onReopen) return;
    setIsReopening(true);
    try {
      await onReopen(inspection.id);
    } finally {
      setIsReopening(false);
      setReopenDialogOpen(false);
    }
  };

  const renderActionButton = () => {
    if (actionModel.primaryAction === "start") {
      return (
        <Button
          size="sm"
          className="gap-1.5"
          onClick={() => onStart!(inspection.id)}
          data-testid={`inspection-start-${inspection.id}`}
        >
          <Play className="h-3.5 w-3.5" />
          {t("inspections.start")}
        </Button>
      );
    }

    if (actionModel.primaryAction === "continue") {
      return (
        <Button
          size="sm"
          className="gap-1.5"
          onClick={() => onContinue!(inspection.id)}
          data-testid={`inspection-continue-${inspection.id}`}
        >
          <Play className="h-3.5 w-3.5" />
          {t("inspections.continue")}
        </Button>
      );
    }

    if (actionModel.primaryAction === "view") {
      return (
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5"
          onClick={() => onView!(inspection.id)}
          data-testid={`inspection-view-${inspection.id}`}
        >
          <Eye className="h-3.5 w-3.5" />
          {/* TES-1705: one term for the one destination — the inspection detail/execution
              view (breadcrumb "Prüfung durchführen"). Replaces the ambiguous "Anzeigen" vs
              "Details anzeigen" split; wording only, onView routing unchanged. */}
          {t("inspections.execute")}
        </Button>
      );
    }

    return null;
  };

  const { menu } = actionModel;

  // TES-1998: Klick auf die Karte öffnet die Detail-Ansicht direkt (kein Menü-Umweg).
  // Interaktive Nachfahren (Start/Continue/View-Buttons, Menü, Assign-Dropdown) behalten
  // ihre eigene Aktion — der closest()-Guard lässt deren Klicks nicht durchfallen.
  // Tastatur-Zugang bleibt über das Aktionsmenü ("Details anzeigen") erhalten.
  const handleCardClick = onView
    ? (e: MouseEvent<HTMLDivElement>) => {
        const el = e.target as HTMLElement;
        if (el.closest("button, a, input, [role='menuitem']")) return;
        onView(inspection.id);
      }
    : undefined;

  return (
    <Card
      className={cn(
        "group transition-all duration-200",
        // Tonal-Hover (DS §4) darf den Overdue-Tint nicht ueberschreiben — bei overdue vertieft der Hover den Rot-Ton
        displayStatus === "overdue"
          ? "border-destructive bg-destructive/5 hover:bg-destructive/10"
          : "hover:bg-surface-container-low",
        onView && "cursor-pointer",
      )}
      onClick={handleCardClick}
      data-testid={`inspection-row-${inspection.id}`}
    >
      <CardContent className={compact ? "p-4" : "p-4 sm:p-6"}>
        <div className="flex flex-col gap-4">
          {/* Header Row - Mobile-first: stacks vertically on small screens */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
            {/* Title and Badges Section */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center h-7 w-7 rounded-md bg-primary/10 text-primary shrink-0 mt-0.5">
                  <FileCheck className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3
                    className={cn("font-semibold break-words", compact ? "text-base" : "text-lg")}
                  >
                    {checklistName}
                  </h3>
                </div>
              </div>

              {/* Badges Row - wraps on all screens */}
              <div className="flex flex-wrap items-center gap-2 mt-2 ml-10">
                <StatusBadge status={displayStatus} />
                {inspection.team_name && (
                  <Badge variant="outline" className="text-xs gap-1">
                    <Users className="h-3 w-3" />
                    {inspection.team_name}
                  </Badge>
                )}
                {inspection.defect_count !== undefined && inspection.defect_count > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-warning/10 text-warning border border-warning/20">
                    <AlertTriangle className="h-3 w-3" />
                    {inspection.defect_count}{" "}
                    {inspection.defect_count === 1 ? t("defects.defect") : t("defects.title")}
                  </span>
                )}
                {/* Warning for unavailable assignee with reassign button */}
                {inspection.isAssigneeUnavailable && inspection.assigned_to && (
                  <div className="inline-flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-warning/10 text-warning border border-warning/20">
                      <AlertCircle className="h-3 w-3" />
                      {!inspection.assigned_profile
                        ? t("tasks.userDeleted")
                        : t("tasks.userDisabled")}
                    </span>
                    {actionModel.assignment.canReassignUnavailableAssignee && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-xs gap-1 text-warning hover:text-warning hover:bg-warning/10"
                          >
                            <UserPlus className="h-3 w-3" />
                            {t("tasks.reassign")}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="max-h-64 overflow-y-auto">
                          {renderAssignableUserItems()}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Actions - stacks below title on mobile, right-aligned on desktop */}
            <div className="flex flex-wrap items-center gap-2 shrink-0 ml-10 sm:ml-0">
              {/* Primary "Assign & start" CTA — surfaces the unassigned-inspection action
                  prominently instead of hiding it (TES-1729 / N7). Permission gate
                  (canAssignUnassigned) is unchanged: only roles with assignable users
                  + the assign callback see it. Opening this dropdown begins the assign
                  flow, the gateway into the execution view. */}
              {actionModel.assignment.canAssignUnassigned && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      className="gap-1.5 bg-info hover:bg-info/90 text-info-foreground"
                      data-testid={`inspection-assign-start-${inspection.id}`}
                    >
                      <UserPlus className="h-4 w-4" />
                      {t("inspections.assignAndStart")}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="max-h-64 overflow-y-auto">
                    {renderAssignableUserItems()}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {renderActionButton()}

              {menu.hasMenuItems && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      data-testid={`inspection-menu-${inspection.id}`}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {menu.showDetails && (
                      <DropdownMenuItem onClick={() => onView!(inspection.id)}>
                        <Eye className="h-4 w-4 mr-2" />
                        {/* TES-1705: navigates into the inspection (onView -> /inspections/:id),
                            where content is filled/edited — label it as such instead of the
                            read-sounding "Details anzeigen". Same term as the primary button. */}
                        {t("inspections.execute")}
                      </DropdownMenuItem>
                    )}
                    {menu.edit && (
                      <DropdownMenuItem
                        data-testid={`inspection-edit-${inspection.id}`}
                        onClick={() => onEdit!(inspection)}
                      >
                        <Pencil className="h-4 w-4 mr-2" />
                        {/* TES-1705: opens EditInspectionDialog (assignee/position/due date/
                            notes only) — not the inspection content. "Stammdaten bearbeiten"
                            makes the metadata-only scope explicit vs. the generic "Bearbeiten". */}
                        {t("inspections.editMasterData")}
                      </DropdownMenuItem>
                    )}
                    {menu.reopen && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleReopenClick}>
                          <RotateCcw className="h-4 w-4 mr-2" />
                          {t("inspections.reopen")}
                        </DropdownMenuItem>
                      </>
                    )}
                    {(menu.cancel || menu.delete) && (
                      <>
                        <DropdownMenuSeparator />
                        {menu.cancel && (
                          <DropdownMenuItem onClick={() => onCancel!(inspection.id)}>
                            <XCircle className="h-4 w-4 mr-2" />
                            {t("inspections.cancelInspection")}
                          </DropdownMenuItem>
                        )}
                        {menu.delete && (
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => onDelete!(inspection.id, checklistName)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            {t("common.delete")}
                          </DropdownMenuItem>
                        )}
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          {/* Context Row - Position and Due Date - stacks on mobile */}
          {(positionName || inspection.due_date) && (
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
              {positionName && (
                <div className="flex items-center gap-1.5 min-w-0">
                  <MapPin className="h-4 w-4 shrink-0" />
                  <span className="font-medium text-foreground break-words">{positionName}</span>
                  {inspection.inspection_positions?.is_archived && (
                    <Badge variant="outline" className="text-xs text-muted-foreground">
                      {t("positions.archivedBadge")}
                    </Badge>
                  )}
                </div>
              )}
              {positionName && inspection.due_date && <span className="hidden sm:inline">•</span>}
              {inspection.due_date && (
                <div className={cn("flex items-center gap-1.5", isOverdue && "text-destructive")}>
                  <Calendar className="h-4 w-4 shrink-0" />
                  <span className="break-words">
                    {t("dashboard.dueDate")}:{" "}
                    {format(new Date(inspection.due_date), "d. MMMM yyyy", { locale: dateLocale })}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Meta Row - Created by, Assigned to (like DefectCard meta row) */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
            {createdName && (
              <div className="flex items-center gap-2">
                <ProfileAvatar profile={inspection.created_profile} size="sm" />
                <span data-sentry-mask>
                  {t("dashboard.createdBy")} {createdName}
                </span>
              </div>
            )}
            {assignedName ? (
              <div className="flex items-center gap-2">
                <ProfileAvatar profile={inspection.assigned_profile} size="sm" />
                <span data-sentry-mask>
                  {t("dashboard.assignedTo")}: {assignedName}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 text-warning">
                <User className="h-4 w-4" />
                <span className="font-medium">{t("dashboard.unassigned")}</span>
              </div>
            )}
          </div>

          {/* Completion Info for completed inspections */}
          {displayStatus === "completed" && inspection.completed_at && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <span>
                  {t("inspections.completedAt")}:{" "}
                  {format(new Date(inspection.completed_at), "d. MMMM yyyy, HH:mm", {
                    locale: dateLocale,
                  })}
                </span>
              </div>
              {assignedName && (
                <div className="flex items-center gap-1.5">
                  <ProfileAvatar profile={inspection.assigned_profile} size="sm" />
                  <span data-sentry-mask>
                    {t("inspections.completedBy")}: {assignedName}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>

      {/* Reopen Confirmation Dialog */}
      <ReopenInspectionDialog
        open={reopenDialogOpen}
        onOpenChange={setReopenDialogOpen}
        onConfirm={handleReopenConfirm}
        isLoading={isReopening}
      />
    </Card>
  );
}
